import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import Http from '@/services/Http';
import type { StatusDefinition, SaveStatusRequest } from '@/models/StatusDefinitionModel';

/**
 * Global Status Store
 * 
 * Manages status definitions across all categories.
 * Provides utilities for value/label/id mapping and easy integration with datatables.
 */
export const useStatusStore = defineStore('status', () => {
  // State
  const statuses = ref<Record<string, StatusDefinition[]>>({});
  const loading = ref<Record<string, boolean>>({});
  const loaded = ref<Record<string, boolean>>({});

  // Categories supported
  const categories = [
    'Request',
    'WorkOrder',
    'Part',
    'Expense',
    'Mileage',
    'Time'
  ];

  // Getters (computed)
  const getStatuses = (category: string): StatusDefinition[] => {
    return statuses.value[category] || [];
  };

  const getStatusById = (category: string, id: number): StatusDefinition | undefined => {
    return statuses.value[category]?.find(s => s.Id === id);
  };

  const getStatusByValue = (category: string, value: number): StatusDefinition | undefined => {
    return statuses.value[category]?.find(s => s.Value === value);
  };

  const getLabelByValue = (category: string, value: number): string => {
    const status = getStatusByValue(category, value);
    return status?.Label || `Status ${value}`;
  };

  const getValueById = (category: string, id: number): number | undefined => {
    const status = getStatusById(category, id);
    return status?.Value;
  };

  const getIdByValue = (category: string, value: number): number | undefined => {
    const status = getStatusByValue(category, value);
    return status?.Id;
  };

  const isLoading = (category: string): boolean => {
    return loading.value[category] || false;
  };

  const isLoaded = (category: string): boolean => {
    return loaded.value[category] || false;
  };

  /**
   * Get active statuses sorted by SortOrder
   */
  const getActiveStatuses = (category: string): StatusDefinition[] => {
    return getStatuses(category)
      .filter(s => s.IsActive)
      .sort((a, b) => a.SortOrder - b.SortOrder);
  };

  /**
   * Get terminal statuses (IsRefActive = false)
   */
  const getTerminalStatuses = (category: string): StatusDefinition[] => {
    return getStatuses(category)
      .filter(s => s.IsActive && s.IsRefActive === false)
      .sort((a, b) => a.SortOrder - b.SortOrder);
  };

  /**
   * Get values array for terminal statuses
   */
  const getTerminalValues = (category: string): number[] => {
    return getTerminalStatuses(category).map(s => s.Value);
  };

  /**
   * Map array of Ids to array of Values
   * Used when sending filter to API
   */
  const mapIdsToValues = (category: string, ids: number[]): number[] => {
    const values: number[] = [];
    for (const id of ids) {
      const value = getValueById(category, id);
      if (value !== undefined) {
        values.push(value);
      }
    }
    return values;
  };

  /**
   * Map array of Values to array of Ids
   * Used when receiving filter from URL/API
   */
  const mapValuesToIds = (category: string, values: number[]): number[] => {
    const ids: number[] = [];
    for (const value of values) {
      const id = getIdByValue(category, value);
      if (id !== undefined) {
        ids.push(id);
      }
    }
    return ids;
  };

  /**
   * Check if a value represents a terminal status
   */
  const isTerminalValue = (category: string, value: number): boolean => {
    const status = getStatusByValue(category, value);
    return status ? !status.IsRefActive : false;
  };

  /**
   * Check if a value is active
   */
  const isActiveValue = (category: string, value: number): boolean => {
    const status = getStatusByValue(category, value);
    return status?.IsActive ?? false;
  };

  // Actions
  const loadStatuses = async (category: string): Promise<void> => {
    if (loading.value[category]) return;
    
    loading.value[category] = true;
    
    try {
      const response = await Http.get(`/Status/List/${category}`);

      if (response && Array.isArray(response)) {
        statuses.value[category] = response;
        loaded.value[category] = true;
      }
    } catch (error) {
      console.error(`Error loading statuses for ${category}:`, error);
      statuses.value[category] = [];
    } finally {
      loading.value[category] = false;
    }
  };

  /**
   * Load all categories at once
   */
  const loadAllCategories = async (): Promise<void> => {
    await Promise.all(categories.map(cat => loadStatuses(cat)));
  };

  /**
   * Refresh (reload) a specific category
   */
  const refreshCategory = async (category: string): Promise<void> => {
    loaded.value[category] = false;
    await loadStatuses(category);
  };

  /**
   * Save a status (create or update)
   */
  const saveStatus = async (status: SaveStatusRequest): Promise<void> => {
    try {
      await Http.post('/Status/Save', status);
      // Refresh the category after saving
      await refreshCategory(status.Category);
    } catch (error) {
      console.error('Error saving status:', error);
      throw error;
    }
  };

  /**
   * Delete (soft delete) a status
   */
  const deleteStatus = async (category: string, id: number): Promise<void> => {
    try {
      await Http.post(`/Status/Delete/${category}/${id}`);
      // Refresh the category after deleting
      await refreshCategory(category);
    } catch (error) {
      console.error('Error deleting status:', error);
      throw error;
    }
  };

  /**
   * Seed default statuses for a category
   */
  const seedDefaults = async (): Promise<void> => {
    try {
      await Http.post('/Status/SeedDefaults');
      // Refresh all categories
      await loadAllCategories();
    } catch (error) {
      console.error('Error seeding defaults:', error);
      throw error;
    }
  };

  /**
   * Get color for a status value
   */
  const getColor = (category: string, value: number): string => {
    const status = getStatusByValue(category, value);
    return status?.Color || '#6c757d';
  };

  /**
   * Get icon for a status value
   */
  const getIcon = (category: string, value: number): string | undefined => {
    const status = getStatusByValue(category, value);
    return status?.Icon;
  };

  return {
    // State
    statuses,
    loading,
    loaded,
    categories,
    
    // Getters
    getStatuses,
    getStatusById,
    getStatusByValue,
    getLabelByValue,
    getValueById,
    getIdByValue,
    getActiveStatuses,
    getTerminalStatuses,
    getTerminalValues,
    isLoading,
    isLoaded,
    isTerminalValue,
    isActiveValue,
    getColor,
    getIcon,
    
    // Utilities
    mapIdsToValues,
    mapValuesToIds,
    
    // Actions
    loadStatuses,
    loadAllCategories,
    refreshCategory,
    saveStatus,
    deleteStatus,
    seedDefaults
  };
});
